import React, { useState } from "react";
import { TableData } from "../types";
import { useKeywordStore } from "../stores/keywordStore";

interface MetaTag {
  name?: string;
  content?: string;
  property?: string;
  charset?: string;
}

interface ApiData {
  title?: string;
  canonical?: string;
  metaTags?: MetaTag[];
  textContent?: string;
  h1?: string;
  intro?: string;
}

interface UrlInputProps {
  onUrlChange: (url: string) => void;
  primaryKeyword: string;
  secondaryKeyword1: string;
  secondaryKeyword2: string;
  onPrimaryKeywordChange: (keyword: string) => void;
  onSecondaryKeyword1Change: (keyword: string) => void;
  onSecondaryKeyword2Change: (keyword: string) => void;
  onTableDataChange?: (data: TableData[]) => void;
  onBodyFetched?: (body: string) => void;
  onSubmit: (calculatePlacement?: boolean) => void;
  url: string;
}

const UrlInput: React.FC<UrlInputProps> = ({
  onUrlChange,
  primaryKeyword,
  secondaryKeyword1,
  secondaryKeyword2,
  onPrimaryKeywordChange,
  onSecondaryKeyword1Change,
  onSecondaryKeyword2Change,
  onTableDataChange,
  onBodyFetched,
  onSubmit,
  url,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { getUrlResponse, setUrlResponse } = useKeywordStore();

  const calculateDensity = (text: string | undefined | null, keyword: string): string => {
    if (!text || !keyword.trim()) {
      console.log(`No text or keyword for "${keyword}": text="${text}", keyword="${keyword}"`);
      return "0%";
    }
    const normalizedText = text.replace(/\s+/g, ' ').trim();
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${escapedKeyword}\\b`, "gi");
    const matches = normalizedText.match(regex);
    const count = matches ? matches.length : 0;
    const words = normalizedText.split(' ').filter(word => word.length > 0);
    const totalWords = words.length;
    const density = totalWords > 0 ? Math.round((count / totalWords) * 100 * 10) / 10 : 0;
    console.log(`Density for "${keyword}": text="${normalizedText.slice(0, 50)}...", matches=`, matches, `count=${count}, totalWords=${totalWords}, density=${density}%`);
    return `${density}%`;
  };

  const extractSlugs = (inputUrl?: string): string[] => {
    const slugs: string[] = [];

    if (inputUrl) {
      try {
        const parsedUrl = new URL(inputUrl);
        const path = parsedUrl.pathname;
        const segments = path.split('/').filter(segment => segment);
        const slug = segments[segments.length - 1] || path.replace(/^\//, '').replace(/\/$/, '') || '';
        if (slug) {
          slugs.push(slug);
        }
      } catch (e) {
        console.log(`Invalid input URL skipped: ${inputUrl}`);
      }
    }

    console.log("Extracted Slugs:", slugs);
    return slugs;
  };

  const checkSlugKeywordPresence = (slugs: string[], keyword: string): string => {
    if (!slugs.length || !keyword.trim()) {
      return "✖";
    }

    const normalizedKeyword = keyword.toLowerCase().replace(/\s+/g, '-');
    const hasKeyword = slugs.some(slug => slug.toLowerCase().includes(normalizedKeyword));

    return hasKeyword ? "✅" : "✖";
  };

  const cleanHtmlForTextExtraction = (doc: Document): string => {
    // Remove unwanted elements
    doc.querySelectorAll('script, style, noscript, iframe, nav, header, footer, aside, .menu, .navigation, .sidebar').forEach(el => el.remove());
    
    // Clean up remaining text
    const walker = doc.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    const nodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode()) !== null) {
      if (node instanceof Text) {
        nodes.push(node);
      }
    }
    
    let cleanedText = nodes
      .map(textNode => textNode.textContent?.trim() || '')
      .filter(text => text.length > 0)
      .join(' ')
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s]/g, ' ') // Remove punctuation, keep words and spaces
      .trim();
    
    // Basic stop words filter to remove common junk (expand as needed)
    const stopWords = new Set([
      'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i', 'it', 'for', 'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at', 'this', 'but', 'his', 'by', 'from', 'they', 'she', 'or', 'an', 'will', 'my', 'one', 'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out', 'if', 'about', 'who', 'get', 'which', 'go', 'me', 'when', 'make', 'can', 'like', 'time', 'no', 'just', 'him', 'know', 'take', 'people', 'into', 'year', 'your', 'good', 'some', 'could', 'them', 'see', 'other', 'than', 'then', 'now', 'look', 'only', 'come', 'its', 'over', 'think', 'also', 'back', 'after', 'use', 'two', 'how', 'our', 'work', 'first', 'well', 'way', 'even', 'new', 'want', 'because', 'any', 'these', 'give', 'day', 'most', 'us',
      // CSS/JS junk
      'px', 'em', 'rem', 'div', 'span', 'class', 'id', 'href', 'src', 'alt', 'title', 'style', 'script', 'link', 'meta', 'head', 'body', 'html', 'doctype', 'cta', 'fx', 'wrapper', 'btn', 'hover', 'important', 'background', 'color', 'form', 'type', 'submit', 'card', 'inline', 'wp', 'content', 'font', 'width', 'height', 'margin', 'padding', 'display', 'border', 'top', 'left', 'min', 'popup', 'services', 'size', 'block', 'radius', 'bottom', 'data', 'seo', 'line', 'url', 'themes', 'more', 'max', 'auto', 'var', 'assets', 'position', 'center', 'no', 'glow', 'right', 'business', 'results', 'quote', 'through', 'not', 'img', 'flex', 'lead', 'team', 'weight', 'before', 'text', 'circle', 'document', 'com', 'inner', 'get', 'fff', 'none', 'management', 'time', 'embed', 'family', 'mcta', 'fonts', 'format', 'woff', 'https', 'www', 'clients', 'align', 'drive', 'approach', 'style', 'normal', 'headshot', 'proposal', 'goals', 'how', 'box', 'shadow', 'absolute', 'index', 'qualified', 'platform', 'ph', 'letter', 'spacing'
    ]);
    
    const words = cleanedText.toLowerCase().split(/\s+/).filter(word => 
      word.length > 2 && !stopWords.has(word) // Min length 3, exclude stops
    );
    
    cleanedText = words.join(' ');
    
    return cleanedText;
  };

  const fetchTableData = async () => {
    if (!url.trim()) {
      setError("Please enter a URL");
      return;
    }

    const isNoKeyword = !primaryKeyword.trim() && !secondaryKeyword1.trim() && !secondaryKeyword2.trim();

    if (!isNoKeyword && !primaryKeyword.trim()) {
      setError("Please enter at least a primary keyword");
      return;
    }

    setLoading(true);
    setError(null);

    let fetchUrl = url;
    let cacheKey = url;

    try {
      const parsed = new URL(url);
      const homeUrl = parsed.origin + '/';
      fetchUrl = isNoKeyword ? homeUrl : url;
      cacheKey = fetchUrl;
    } catch (e) {
      console.log(`Invalid URL, using original: ${url}`);
    }

    try {
      // Check cache first
      const cachedResponse = getUrlResponse(cacheKey);
      let apiData: ApiData = {};

      if (cachedResponse) {
        console.log("Using cached API response for URL:", cacheKey);
        apiData = cachedResponse.data || {};
      } else {
        // Use CORS proxy to fetch HTML
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(fetchUrl)}`;
        console.log("Fetching from proxy:", proxyUrl);

        const response = await fetch(proxyUrl, {
          headers: {
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();

        console.log("Fetched HTML length:", html.length);

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Clean the document for better text extraction
        const cleanedTextContent = cleanHtmlForTextExtraction(doc);

        const title = doc.querySelector('title')?.textContent?.trim() || '';
        const metaTags = Array.from(doc.querySelectorAll('meta')).map(meta => ({
          name: meta.getAttribute('name') ?? undefined,
          content: meta.getAttribute('content') ?? undefined,
          property: meta.getAttribute('property') ?? undefined,
          charset: meta.getAttribute('charset') ?? undefined,
        }));

        const h1Element = doc.querySelector('h1');
        const h1 = h1Element ? h1Element.textContent?.trim() || '' : '';

        let intro = '';
        const firstP = doc.querySelector('p');
        if (firstP) {
          intro = firstP.textContent?.trim() || '';
        }
        if (!intro || intro.split(/\s+/).filter(w => w.length > 0).length < 50) {
          const words = cleanedTextContent.split(/\s+/).filter(w => w.length > 0).slice(0, 250);
          intro = words.join(' ');
        }

        apiData = {
          title,
          metaTags,
          textContent: cleanedTextContent,
          h1,
          intro,
          canonical: '', // Not used
        };

        setUrlResponse(cacheKey, { success: true, message: 'Parsed HTML', data: apiData });
      }

      const metaDescTag = apiData.metaTags?.find(tag => tag.name?.toLowerCase() === "description");
      const metaDesc = metaDescTag ? metaDescTag.content || "" : "";
      const metaTitle = apiData.title || "";
      const body = apiData.textContent || "";
      const intro = apiData.intro || "";
      const h1Tag = apiData.h1 || "";

      onBodyFetched?.(body);
      if (isNoKeyword) {
        
        onTableDataChange?.([]);
      } else {
        // Extract slugs from original input URL for placement check
        const slugs = extractSlugs(url);

        // Process keywords
        const keywords = [primaryKeyword.trim(), secondaryKeyword1.trim(), secondaryKeyword2.trim()].filter(kw => kw);
        console.log("Keywords:", keywords);

        // Calculate placement data
        const placementData: TableData[] = keywords.map(keyword => {
          const data = {
            keyword,
            intro: calculateDensity(intro, keyword),
            body: calculateDensity(body, keyword),
            h1: calculateDensity(h1Tag, keyword),
            slug: checkSlugKeywordPresence(slugs, keyword),
            desc: calculateDensity(metaDesc, keyword),
            title: calculateDensity(metaTitle, keyword),
          };
          console.log(`Keyword "${keyword}" Placement:`, data);
          return data;
        });

        // Update table data
        if (onTableDataChange) {
          console.log("Sending placementData to parent:", placementData);
          onTableDataChange(placementData);
        }
      }

      onSubmit(true);
    } catch (err: any) {
      // Enhanced error handling
      let errorMessage = "Failed to fetch data from the page. Please check the URL and try again.";
      if (err.message.includes('HTTP error')) {
        errorMessage = `Fetch error: ${err.message}`;
      }
      console.error("Fetch Error Details:", err);
      setError(errorMessage);
      if (onTableDataChange) {
        onTableDataChange([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>URL</label>
        <input
          type="url"
          placeholder="Enter URL"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Primary Keyword</label>
        <input
          type="text"
          value={primaryKeyword}
          onChange={(e) => onPrimaryKeywordChange(e.target.value)}
          placeholder="e.g., digital marketing"
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Secondary Keyword 1</label>
        <input
          type="text"
          value={secondaryKeyword1}
          onChange={(e) => onSecondaryKeyword1Change(e.target.value)}
          placeholder="e.g., SEO optimization"
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Secondary Keyword 2</label>
        <input
          type="text"
          value={secondaryKeyword2}
          onChange={(e) => onSecondaryKeyword2Change(e.target.value)}
          placeholder="e.g., content marketing"
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      
      <button
        onClick={fetchTableData}
        disabled={loading || !url.trim()}
        style={{
          padding: "0.75rem 1.5rem",
          backgroundColor: loading || !url.trim() ? "#ccc" : "#6B9A30",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        {loading ? "Loading..." : "Check Keyword Density"}
      </button>
      {error && (
        <div
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "1rem",
            borderRadius: "4px",
            marginTop: "1rem",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default UrlInput;